import { useState, useCallback, memo, type CSSProperties } from 'react'
import { aiApi } from '../../lib/api.js'
import { usePrograms } from '../../store/index.js'
import type { AiSuggestedProgram, AiSuggestedAd } from '../../types/domain.js'
import { SuggestionOverlay } from './SuggestionOverlay.js'
import { DrawerShell } from '../common/DrawerShell.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'

type Phase = 'PROGRAMS' | 'ADS'
type LoadState = 'idle' | 'loading' | 'done' | 'error'

interface SmartGridDrawerProps {
  gridId:    string
  channelId: string
  date:      string
  onClose:   () => void
  onApplied?: () => void
}

export const SmartGridDrawer = memo(function SmartGridDrawer({
  gridId, channelId, date, onClose, onApplied,
}: SmartGridDrawerProps) {
  const [phase, setPhase]                               = useState<Phase>('PROGRAMS')
  const [loadState, setLoadState]                       = useState<LoadState>('idle')
  const [error, setError]                               = useState<string | null>(null)
  const [customInstructions, setCustomInstructions]     = useState('')

  // Phase programmes
  const [programSuggestions, setProgramSuggestions]   = useState<AiSuggestedProgram[]>([])
  const [acceptedPrograms, setAcceptedPrograms]        = useState<Set<number>>(new Set())
  const [programSuggestionId, setProgramSuggestionId] = useState<string | null>(null)

  // Phase pub
  const [adSuggestions, setAdSuggestions]   = useState<AiSuggestedAd[]>([])
  const [acceptedAds, setAcceptedAds]       = useState<Set<number>>(new Set())
  const [adSuggestionId, setAdSuggestionId] = useState<string | null>(null)

  // Pré-sélection catalogue
  const allPrograms                                       = usePrograms()
  const [selectedProgramIds, setSelectedProgramIds]       = useState<string[]>([])
  const [showCatalog, setShowCatalog]                     = useState(false)

  const toggleProgram = useCallback((id: string) => {
    setSelectedProgramIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }, [])

  const toggleAcceptProgram = useCallback((i: number) => {
    setAcceptedPrograms(prev => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n })
  }, [])

  const toggleAcceptAd = useCallback((i: number) => {
    setAcceptedAds(prev => { const n = new Set(prev); if (n.has(i)) n.delete(i); else n.add(i); return n })
  }, [])

  const handleGeneratePrograms = useCallback(async () => {
    setLoadState('loading')
    setError(null)
    try {
      const result = await aiApi.suggestPrograms({
        channelId,
        gridId,
        date,
        customInstructions: customInstructions.trim() || undefined,
        programIds: selectedProgramIds.length > 0 ? selectedProgramIds : undefined,
      })
      setProgramSuggestions(result.suggestions)
      setProgramSuggestionId(result.suggestionId ?? null)
      // Pré-accepte les suggestions à confiance >= 0.7
      setAcceptedPrograms(new Set(
        result.suggestions.map((s, i) => ({ s, i })).filter(({ s }) => s.confidence >= 0.7).map(({ i }) => i)
      ))
      setLoadState('done')
    } catch (err: any) {
      setError(err.message)
      setLoadState('error')
    }
  }, [channelId, gridId, date, customInstructions, selectedProgramIds])

  const handleApplyPrograms = useCallback(async () => {
    if (!programSuggestionId || acceptedPrograms.size === 0) return
    setLoadState('loading')
    try {
      await aiApi.apply(programSuggestionId, [...acceptedPrograms])
      onApplied?.()
      setPhase('ADS')
      setLoadState('idle')
    } catch (err: any) {
      setError(err.message)
      setLoadState('error')
    }
  }, [programSuggestionId, acceptedPrograms, onApplied])

  const handleGenerateAds = useCallback(async () => {
    setLoadState('loading')
    setError(null)
    try {
      const result = await aiApi.suggestAds({
        channelId, gridId, date,
        customInstructions: customInstructions.trim() || undefined,
      })
      setAdSuggestions(result.suggestions)
      setAdSuggestionId(result.suggestionId ?? null)
      setAcceptedAds(new Set(
        result.suggestions.map((s, i) => ({ s, i })).filter(({ s }) => s.confidence >= 0.7).map(({ i }) => i)
      ))
      setLoadState('done')
    } catch (err: any) {
      setError(err.message)
      setLoadState('error')
    }
  }, [channelId, gridId, date, customInstructions])

  const handleApplyAds = useCallback(async () => {
    if (!adSuggestionId || acceptedAds.size === 0) return
    setLoadState('loading')
    try {
      await aiApi.apply(adSuggestionId, [...acceptedAds])
      onApplied?.()
      onClose()
    } catch (err: any) {
      setError(err.message)
      setLoadState('error')
    }
  }, [adSuggestionId, acceptedAds, onApplied, onClose])

  const handlePhaseChange = useCallback((nextPhase: Phase) => {
    setPhase(nextPhase)
    setLoadState('idle')
  }, [])

  const handleCatalogToggle = useCallback(() => setShowCatalog(value => !value), [])
  const handleReset = useCallback(() => setLoadState('idle'), [])

  const isLoading    = loadState === 'loading'
  const acceptedSize = phase === 'PROGRAMS' ? acceptedPrograms.size : acceptedAds.size

  return (
    <>
      <DrawerShell className="smart-grid-drawer" closeDisabled={isLoading} onClose={onClose} title="Grille intelligente" titlePrefix={<span className="smart-grid__spark" aria-hidden="true">✦</span>}>
          {/* Onglets phase */}
          <div className="smart-grid__tabs" role="tablist" aria-label="Phase de suggestion">
            {(['PROGRAMS', 'ADS'] as Phase[]).map(p => (
              <button
                type="button"
                key={p}
                className={`smart-grid__tab${phase === p ? ' is-active' : ''}`}
                role="tab"
                aria-selected={phase === p}
                onClick={() => handlePhaseChange(p)}
              >
                {p === 'PROGRAMS' ? '① Programmes' : '② Publicités'}
              </button>
            ))}
          </div>

        {/* Contenu scrollable */}
        <div className="smart-grid__body">

          {/* Instructions libres */}
          <div className="smart-grid__instructions">
            <label className="agenda-editor__eyebrow" htmlFor="smart-grid-instructions">
              Instructions pour l'IA (optionnel)
            </label>
            <textarea
              id="smart-grid-instructions"
              className="tp-textarea smart-grid__textarea"
              value={customInstructions}
              onChange={e => setCustomInstructions(e.target.value)}
              placeholder={phase === 'PROGRAMS'
                ? 'Ex : Favorise les documentaires cet après-midi…'
                : 'Ex : Répartis équitablement, évite deux fois le même annonceur…'
              }
              rows={3}
            />
          </div>

          {/* ── Phase PROGRAMMES ──────────────────────────────────────────────── */}
          {phase === 'PROGRAMS' && (
            <>
              {/* Pré-sélection catalogue */}
              <div className="smart-grid__catalog-section">
                <button
                  type="button"
                  className="smart-grid__link-button"
                  onClick={handleCatalogToggle}
                  aria-expanded={showCatalog}
                >
                  {showCatalog ? '▼' : '▶'} Pré-sélectionner des programmes ({selectedProgramIds.length} sélectionné{selectedProgramIds.length !== 1 ? 's' : ''})
                </button>
                {showCatalog && (
                  <div className="smart-grid__catalog-list">
                    {allPrograms.map(p => (
                      <label key={p.id} className="smart-grid__catalog-row">
                        <input
                          type="checkbox"
                          checked={selectedProgramIds.includes(p.id)}
                          onChange={() => toggleProgram(p.id)}
                          className="smart-grid__checkbox-input"
                        />
                        <div className="smart-grid__catalog-main">
                          <div className="smart-grid__catalog-name">{p.title}</div>
                          <div className="smart-grid__catalog-meta">
                            {p.type} · {Math.round(p.durationSec / 60)}min{p.isNew ? ' · INÉDIT' : ''}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {loadState === 'idle' && (
                <button type="button" className="smart-grid__primary" onClick={handleGeneratePrograms}>
                  ✦ Analyser et suggérer
                </button>
              )}
              {isLoading && <LoadingCard message="L'IA analyse la grille et le catalogue…" />}
              {loadState === 'error' && error && <ErrorCard message={error} onRetry={handleReset} />}
              {loadState === 'done' && programSuggestions.length === 0 && (
                <EmptyCard message="Aucune suggestion générée. La grille est peut-être déjà complète." />
              )}
              {loadState === 'done' && programSuggestions.length > 0 && (
                <>
                  <div className="smart-grid__results-summary">
                    <span>{programSuggestions.length} suggestion{programSuggestions.length > 1 ? 's' : ''} · {acceptedPrograms.size} sélectionnée{acceptedPrograms.size > 1 ? 's' : ''}</span>
                    <button type="button" className="smart-grid__link-button" onClick={handleReset}>↺ Regénérer</button>
                  </div>
                  {programSuggestions.map((s, i) => (
                    <ProgramSuggestionCard key={i} suggestion={s} index={i} accepted={acceptedPrograms.has(i)} onToggle={toggleAcceptProgram} />
                  ))}
                </>
              )}
            </>
          )}

          {/* ── Phase PUB ─────────────────────────────────────────────────────── */}
          {phase === 'ADS' && (
            <>
              {loadState === 'idle' && (
                <button type="button" className="smart-grid__primary" onClick={handleGenerateAds}>
                  ✦ Analyser et placer les spots
                </button>
              )}
              {isLoading && <LoadingCard message="L'IA répartit les spots sur les coupures…" />}
              {loadState === 'error' && error && <ErrorCard message={error} onRetry={handleReset} />}
              {loadState === 'done' && adSuggestions.length === 0 && (
                <EmptyCard message="Aucune suggestion. Vérifiez les campagnes actives et les coupures pub." />
              )}
              {loadState === 'done' && adSuggestions.length > 0 && (
                <>
                  <div className="smart-grid__results-summary">
                    <span>{adSuggestions.length} placement{adSuggestions.length > 1 ? 's' : ''} suggéré{adSuggestions.length > 1 ? 's' : ''} · {acceptedAds.size} sélectionné{acceptedAds.size > 1 ? 's' : ''}</span>
                    <button type="button" className="smart-grid__link-button" onClick={handleReset}>↺ Regénérer</button>
                  </div>
                  {adSuggestions.map((s, i) => (
                    <AdSuggestionCard key={i} suggestion={s} index={i} accepted={acceptedAds.has(i)} onToggle={toggleAcceptAd} />
                  ))}
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {loadState === 'done' && (
          <div className="smart-grid__footer">
            <button type="button" className="tp-button" onClick={onClose}>Annuler</button>
            <button
              type="button"
              className="smart-grid__primary smart-grid__apply"
              onClick={phase === 'PROGRAMS' ? handleApplyPrograms : handleApplyAds}
              disabled={acceptedSize === 0}
            >
              Appliquer {acceptedSize} suggestion{acceptedSize > 1 ? 's' : ''} →
            </button>
          </div>
        )}
      </DrawerShell>

      {/* Overlay de prévisualisation sur la grille */}
      {loadState === 'done' && phase === 'PROGRAMS' && programSuggestions.length > 0 && (
        <SuggestionOverlay
          suggestions={programSuggestions}
          acceptedIndexes={acceptedPrograms}
          onToggle={toggleAcceptProgram}
        />
      )}
    </>
  )
})

// ─── Sous-composants ──────────────────────────────────────────────────────────

const ProgramSuggestionCard = memo(function ProgramSuggestionCard({
  suggestion, index, accepted, onToggle,
}: { suggestion: AiSuggestedProgram; index: number; accepted: boolean; onToggle: (i: number) => void }) {
  const startH = new Date(suggestion.startTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  const endH   = new Date(suggestion.endTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  const dur    = Math.round(suggestion.durationSec / 60)
  const handleToggle = useCallback(() => onToggle(index), [index, onToggle])

  return (
    <button type="button" className={`smart-grid-suggestion is-program${accepted ? ' is-accepted' : ''}`} onClick={handleToggle} aria-pressed={accepted}>
      <div className="smart-grid-suggestion__layout">
        <Checkbox accepted={accepted} variant="program" />
        <div className="smart-grid-suggestion__main">
          <div className="smart-grid-suggestion__title">{suggestion.programTitle}</div>
          <div className="smart-grid-suggestion__time">
            {startH} → {endH} · {dur}min
          </div>
          <div className="smart-grid-suggestion__reason">{suggestion.reason}</div>
          <ConfidenceBar value={suggestion.confidence} variant="program" />
        </div>
      </div>
    </button>
  )
})

const AdSuggestionCard = memo(function AdSuggestionCard({
  suggestion, index, accepted, onToggle,
}: { suggestion: AiSuggestedAd; index: number; accepted: boolean; onToggle: (i: number) => void }) {
  const startH = new Date(suggestion.startTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  const handleToggle = useCallback(() => onToggle(index), [index, onToggle])

  return (
    <button type="button" className={`smart-grid-suggestion is-ad${accepted ? ' is-accepted' : ''}`} onClick={handleToggle} aria-pressed={accepted}>
      <div className="smart-grid-suggestion__layout">
        <Checkbox accepted={accepted} variant="ad" />
        <div className="smart-grid-suggestion__main">
          <div className="smart-grid-suggestion__title">
            {formatAdSpotLabel({ title: suggestion.spotTitle, campaignName: suggestion.campaignName })}
          </div>
          <div className="smart-grid-suggestion__time">{startH}</div>
          <div className="smart-grid-suggestion__reason">{suggestion.reason}</div>
          <ConfidenceBar value={suggestion.confidence} variant="ad" />
        </div>
      </div>
    </button>
  )
})

const Checkbox = memo(function Checkbox({ accepted, variant }: { accepted: boolean; variant: 'program' | 'ad' }) {
  return (
    <div className={`smart-grid-check is-${variant}${accepted ? ' is-accepted' : ''}`} aria-hidden="true">
      {accepted && <span>✓</span>}
    </div>
  )
})

const ConfidenceBar = memo(function ConfidenceBar({ value, variant }: { value: number; variant: 'program' | 'ad' }) {
  const pct = Math.round(value * 100)
  return (
    <div className={`smart-grid-confidence is-${variant}`}>
      <div className="smart-grid-confidence__track">
        <div className="smart-grid-confidence__value" style={{ '--confidence': `${pct}%` } as CSSProperties} />
      </div>
      <span>{pct}%</span>
    </div>
  )
})

const LoadingCard = memo(function LoadingCard({ message }: { message: string }) {
  return (
    <div className="smart-grid-state" data-state="loading">
      <div className="smart-grid-state__icon" aria-hidden="true">✦</div>
      {message}
    </div>
  )
})

const ErrorCard = memo(function ErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="smart-grid-state" data-state="error" role="alert">
      <div className="smart-grid-state__message">{message}</div>
      <button type="button" className="tp-button tp-button--danger-outline" onClick={onRetry}>
        Réessayer
      </button>
    </div>
  )
})

const EmptyCard = memo(function EmptyCard({ message }: { message: string }) {
  return (
    <div className="smart-grid-state" data-state="empty">{message}</div>
  )
})
